export function getProjectHubContextKey({ id, user, lang, permMap }) {
  const permissions = Object.keys(permMap || {}).sort().map((key) => [key, !!permMap[key]]);
  const teams = Array.isArray(user?.teams) ? user.teams.filter(Boolean).sort() : [];
  const assisting = user?.assisting ? {
    username: user.assisting.username || "",
    name: user.assisting.name || "",
    nameZh: user.assisting.nameZh || "",
    salesName: user.assisting.salesName || "",
    region: user.assisting.region || "",
  } : null;

  return JSON.stringify({
    id: id || "",
    username: user?.username || "",
    role: user?.role || "",
    baseRole: user?.baseRole || "",
    customRoleId: user?.customRoleId || "",
    name: user?.name || "",
    nameZh: user?.nameZh || "",
    salesName: user?.salesName || "",
    positionRole: user?.positionRole || "",
    region: user?.region || "",
    team: user?.team || "",
    teams,
    designScope: user?.designScope || "self",
    assistantUnbound: !!user?.assistantUnbound,
    assisting,
    impersonatedBy: user?.impersonatedBy || "",
    lang: lang || "",
    permissions,
  });
}

export function isHubRequestCanceled(error) {
  return error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError"
    || (error?.code === "ECONNABORTED" && !/timeout/i.test(error?.message || ""));
}

export function shouldIgnoreHubLoad({ currentGeneration, requestGeneration, sequence, terminal, canceled }) {
  if (currentGeneration !== requestGeneration) return true;
  if (terminal?.generation !== requestGeneration) return false;
  return sequence < terminal.sequence || (canceled && terminal.generation === requestGeneration);
}

export function resetProjectHubState({ setData, setErr, setErrStatus, setLoadBusy, setAiBusy }) {
  setData(null);
  setErr("");
  setErrStatus(null);
  setLoadBusy(false);
  setAiBusy(false);
}

export async function loadProjectHub({
  api,
  id,
  zh,
  contextRef,
  sequenceRef,
  terminalRef,
  setData,
  setErr,
  setErrStatus,
  setLoadBusy,
}) {
  const { generation, key: requestContextKey } = contextRef.current;
  const sequence = ++sequenceRef.current;
  const isCurrentContext = () => contextRef.current.generation === generation && contextRef.current.key === requestContextKey;
  setLoadBusy(true);
  try {
    const { data } = await api.get(`/projects/${id}/hub`);
    const latestTerminal = terminalRef.current;
    if (!isCurrentContext() || shouldIgnoreHubLoad({
      currentGeneration: contextRef.current.generation,
      requestGeneration: generation,
      sequence,
      terminal: latestTerminal,
      canceled: false,
    })) return null;
    terminalRef.current = { generation, sequence };
    setData(data);
    setErr("");
    setErrStatus(null);
    return data;
  } catch (error) {
    const latestTerminal = terminalRef.current;
    const canceled = isHubRequestCanceled(error);
    if (!isCurrentContext() || shouldIgnoreHubLoad({
      currentGeneration: contextRef.current.generation,
      requestGeneration: generation,
      sequence,
      terminal: latestTerminal,
      canceled,
    })) return null;
    if (!canceled) terminalRef.current = { generation, sequence };

    const status = error?.response?.status ?? null;
    const timeout = error?.code === "ETIMEDOUT" || (error?.code === "ECONNABORTED" && /timeout/i.test(error?.message || ""));
    const detail = error?.response?.data?.detail;
    const fallback = status === 404 ? (zh ? "请确认项目链接是否正确，或返回列表查找。" : "Check the project link or return to the list.")
      : timeout ? (zh ? "加载超时，请重试。" : "The request timed out. Please retry.")
      : canceled ? (zh ? "加载请求已中断，请重试。" : "The request was interrupted. Please retry.")
      : !status ? (zh ? "网络连接暂时不可用，请检查连接后重试。" : "The connection is unavailable. Check your network and retry.")
      : (zh ? "暂时无法加载项目资料，请稍后重试。" : "Unable to load this project. Please retry shortly.");
    setErrStatus(status);
    setErr(typeof detail === "string" && detail ? detail : fallback);
    return null;
  } finally {
    if (isCurrentContext() && sequence === sequenceRef.current) setLoadBusy(false);
  }
}
